<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CountryData } from 'country-codes-list'
import { useAxios } from '@/composables/useAxios'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { getAllCountryCodesData } from '@/composables/useGeo'
import { formatPhone, isCanonicalPhone, parsePastedPhone } from '@/utils/phone'
import { handleAxiosError, getErrorMessage, getBackendMessage } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { AccountDetails, type PaginatedResponse } from '@/types'
import {
  ImportVServerRequest,
  ImportVServerResult,
  VServerNodeList,
  type VServerImportAccountOption,
  type VServerNode,
  type ImportVServerResult as ImportVServerResultType
} from '@/types/vserverImport'

const { t } = useI18n()
const notifyStore = useNotifyStore()

// --- account picker ---------------------------------------------------------
const accountMode = ref<'existing' | 'new'>('existing')
const accounts = ref<VServerImportAccountOption[]>([])
const selectedAccountId = ref<number | null>(null)

// new-account fields
const acc = ref({
  email: '',
  name: '',
  surname: '',
  org: '',
  line1: '',
  city: '',
  postcode: '',
  country_iso: 'DE',
  state: ''
})

const stripeCustomerId = ref('')
const cancelStripeSubId = ref('')

// --- phone -----------------------------------------------------------------
// The API only accepts the canonical +CC.LOCAL form. Splitting the country code
// off into its own picker means the operator cannot enter an unparseable number
// in the first place.
const countryCodes = ref<CountryData[]>([])
// Keyed by ISO country, not by calling code: calling codes are not unique
// (US/CA share +1, RU/KZ share +7), and a plain string model keeps the picker
// free of the object round-tripping that silently drops the operator's pick.
const phoneCountryIso = ref('DE')
const phoneLocal = ref('')
const knownCallingCodes = computed(() => countryCodes.value.map((c) => c.countryCallingCode))
const phoneCodeItems = computed(() =>
  countryCodes.value.map((c) => ({
    value: c.countryCode,
    title: `${c.flag} +${c.countryCallingCode}`,
    props: { subtitle: c.countryNameEn }
  }))
)
const phoneCountry = computed(
  () => countryCodes.value.find((c) => c.countryCode === phoneCountryIso.value) || null
)
const phoneDialCode = computed(() => `+${phoneCountry.value?.countryCallingCode || '49'}`)
const formattedPhone = computed(() => formatPhone(phoneDialCode.value, phoneLocal.value))
// Only consulted on the new-account path, where a phone is mandatory.
const phoneValid = computed(() => isCanonicalPhone(formattedPhone.value))

// A pasted international number carries its own country code, so adopt it
// rather than gluing it onto the picked one.
function adoptPastedPhone(input: string) {
  const { countryCode, local } = parsePastedPhone(
    input,
    phoneDialCode.value,
    knownCallingCodes.value
  )
  // Several countries share a calling code (+1, +7), so only reassign when the
  // parsed code differs from the one already picked. Otherwise pasting a US
  // number would relabel the field Canada.
  if (countryCode !== phoneDialCode.value) {
    const match = countryCodes.value.find((c) => `+${c.countryCallingCode}` === countryCode)
    if (match) phoneCountryIso.value = match.countryCode
  }
  phoneLocal.value = local
}

function handlePhoneInput() {
  if (phoneLocal.value.startsWith('+') || phoneLocal.value.startsWith('00')) {
    adoptPastedPhone(phoneLocal.value)
  }
}

function handlePhonePaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text') || ''
  if (pasted.startsWith('+') || pasted.startsWith('00') || /^\d{10,}$/.test(pasted.trim())) {
    event.preventDefault()
    adoptPastedPhone(pasted)
  }
}

// Keep the dial code in step with the billing country the operator types.
watch(
  () => acc.value.country_iso,
  (iso) => {
    const code = iso.trim().toUpperCase()
    if (code.length !== 2) return
    if (countryCodes.value.some((c) => c.countryCode === code)) phoneCountryIso.value = code
  }
)

// --- VM fields --------------------------------------------------------------
const vm = ref({
  proxmox_node: '',
  proxmox_vmid: null as number | null,
  ipv4: '',
  ipv6_block: '',
  subnet: '',
  gateway: '',
  hostname: '',
  template_slug: ''
})
// Proxmox nodes for the picker. The node is a bare cluster name (`pve1`), not
// the API host, which is exactly the kind of thing an operator should not have
// to remember, so offer the real list and only fall back to free text when
// Proxmox can't be reached.
const nodes = ref<VServerNode[]>([])
const nodesLoaded = ref(false)
const nodesUnavailable = computed(() => nodesLoaded.value && nodes.value.length === 0)
const nodeItems = computed(() =>
  nodes.value.map((n) => ({ value: n.name, title: n.name, props: { subtitle: n.status } }))
)

const monthlyGrossEuro = ref<number | null>(null)
const billingFreq = ref<'monthly' | 'annually'>('monthly')
// optional spec overrides (left blank → read from the live VM)
const specs = ref({
  vcpu: null as number | null,
  memory_mb: null as number | null,
  disk_gb: null as number | null
})

const submitting = ref(false)
const result = ref<ImportVServerResultType | null>(null)

const accountModeItems = computed(() => [
  { value: 'existing', title: t('dashboard.vserver.admin.import.existingAccount') },
  { value: 'new', title: t('dashboard.vserver.admin.import.newAccount') }
])
const billingFreqItems = computed(() => [
  { value: 'monthly', title: t('dashboard.vserver.admin.import.freqMonthly') },
  { value: 'annually', title: t('dashboard.vserver.admin.import.freqAnnually') }
])
const accountItems = computed(() =>
  accounts.value.map((a) => ({ value: a.id, title: `${a.email} (#${a.id})` }))
)

const canSubmit = computed(() => {
  if (submitting.value) return false
  if (!vm.value.proxmox_node.trim() || vm.value.proxmox_vmid === null) return false
  if (!vm.value.ipv4.trim()) return false
  if (!monthlyGrossEuro.value || monthlyGrossEuro.value <= 0) return false
  if (accountMode.value === 'existing') return selectedAccountId.value !== null
  return acc.value.email.trim() !== '' && acc.value.line1.trim() !== '' && phoneValid.value
})

async function fetchAccounts() {
  try {
    const res = await useAxios<null, PaginatedResponse<AccountDetails>>(
      'GET',
      '/account/all?limit=200',
      true
    )
    if (res?.status === STATUS_CODES.OK && res.data?.data) {
      accounts.value = res.data.data
        .filter((a): a is AccountDetails & { id: number } => typeof a.id === 'number')
        .map((a) => ({ id: a.id, email: a.email }))
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerImport:accounts' })
  }
}

async function fetchNodes() {
  try {
    const res = await useAxios<null, unknown>('GET', '/admin/vserver/nodes', true)
    const parsed = VServerNodeList.safeParse(res?.data)
    if (res?.status === STATUS_CODES.OK && parsed.success) {
      nodes.value = parsed.data
      // Single-node cluster: nothing to choose, so pre-fill it.
      if (nodes.value.length === 1) vm.value.proxmox_node = nodes.value[0].name
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerImport:nodes' })
  } finally {
    nodesLoaded.value = true
  }
}

function buildRequest(): ImportVServerRequest | null {
  const body: ImportVServerRequest = {
    proxmox_node: vm.value.proxmox_node.trim(),
    proxmox_vmid: vm.value.proxmox_vmid as number,
    ipv4: vm.value.ipv4.trim(),
    monthly_gross_cents: Math.round((monthlyGrossEuro.value as number) * 100),
    billing_freq: billingFreq.value,
    ...(vm.value.ipv6_block.trim() ? { ipv6_block: vm.value.ipv6_block.trim() } : {}),
    ...(vm.value.subnet.trim() ? { subnet: vm.value.subnet.trim() } : {}),
    ...(vm.value.gateway.trim() ? { gateway: vm.value.gateway.trim() } : {}),
    ...(vm.value.hostname.trim() ? { hostname: vm.value.hostname.trim() } : {}),
    ...(vm.value.template_slug.trim() ? { template_slug: vm.value.template_slug.trim() } : {}),
    ...(specs.value.vcpu ? { vcpu: specs.value.vcpu } : {}),
    ...(specs.value.memory_mb ? { memory_mb: specs.value.memory_mb } : {}),
    ...(specs.value.disk_gb ? { disk_gb: specs.value.disk_gb } : {}),
    ...(stripeCustomerId.value.trim() ? { stripe_customer_id: stripeCustomerId.value.trim() } : {}),
    ...(cancelStripeSubId.value.trim()
      ? { cancel_stripe_subscription_id: cancelStripeSubId.value.trim() }
      : {})
  }

  if (accountMode.value === 'existing') {
    body.account_id = selectedAccountId.value as number
  } else {
    body.account = {
      email: acc.value.email.trim(),
      line1: acc.value.line1.trim(),
      city: acc.value.city.trim(),
      postcode: acc.value.postcode.trim(),
      country_iso: acc.value.country_iso.trim().toUpperCase(),
      phone: formattedPhone.value,
      ...(acc.value.name.trim() ? { name: acc.value.name.trim() } : {}),
      ...(acc.value.surname.trim() ? { surname: acc.value.surname.trim() } : {}),
      ...(acc.value.org.trim() ? { org: acc.value.org.trim() } : {}),
      ...(acc.value.state.trim() ? { state: acc.value.state.trim() } : {})
    }
  }

  const parsed = ImportVServerRequest.safeParse(body)
  if (!parsed.success) {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.admin.import.title'),
      message: t('dashboard.vserver.admin.import.invalidForm')
    })
    return null
  }
  return parsed.data
}

async function submit() {
  const body = buildRequest()
  if (!body) return
  submitting.value = true
  result.value = null
  try {
    const res = await useAxios<ImportVServerRequest, unknown>(
      'POST',
      '/admin/vserver/import',
      true,
      body
    )
    const parsed = ImportVServerResult.safeParse(res?.data)
    if (!parsed.success) {
      throw new Error('unexpected response')
    }
    result.value = parsed.data
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.vserver.admin.import.title'),
      message: parsed.data.existing
        ? t('dashboard.vserver.admin.import.alreadyImported')
        : t('dashboard.vserver.admin.import.success')
    })
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerImport:submit' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('dashboard.vserver.errors.actionFailed'),
      message: getBackendMessage(err) ?? getErrorMessage(err)
    })
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  // Already sorted by country name by useGeo.
  countryCodes.value = getAllCountryCodesData()
  void fetchAccounts()
  void fetchNodes()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.import.title')"
    :subtitle="t('dashboard.vserver.admin.import.subtitle')"
    icon="mdi-import"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminVServer' }"
  >
    <!-- Account -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.vserver.admin.import.accountTitle') }}</h3>
      </div>
      <div class="form-grid">
        <v-select
          v-model="accountMode"
          :items="accountModeItems"
          :label="$t('dashboard.vserver.admin.import.accountMode')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input"
        />
        <v-select
          v-if="accountMode === 'existing'"
          v-model="selectedAccountId"
          :items="accountItems"
          :label="$t('dashboard.vserver.admin.import.selectAccount')"
          variant="outlined"
          density="compact"
          hide-details
          class="form-input wide"
        />
      </div>

      <div v-if="accountMode === 'new'" class="form-grid newacc">
        <v-text-field v-model="acc.email" :label="$t('dashboard.vserver.admin.import.email')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.name" :label="$t('dashboard.vserver.admin.import.name')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.surname" :label="$t('dashboard.vserver.admin.import.surname')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.org" :label="$t('dashboard.vserver.admin.import.org')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.line1" :label="$t('dashboard.vserver.admin.import.line1')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.city" :label="$t('dashboard.vserver.admin.import.city')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.postcode" :label="$t('dashboard.vserver.admin.import.postcode')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="acc.country_iso" :label="$t('dashboard.vserver.admin.import.country')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model="acc.state" :label="$t('dashboard.vserver.admin.import.state')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <div class="form-input phone-row">
          <v-select v-model="phoneCountryIso" :items="phoneCodeItems" :label="$t('dashboard.vserver.admin.import.phoneCode')" variant="outlined" density="compact" hide-details class="phone-code" :menu-props="{ maxHeight: 300, minWidth: '260px' }" />
          <v-text-field v-model="phoneLocal" type="tel" :label="$t('dashboard.vserver.admin.import.phone')" variant="outlined" density="compact" hide-details class="phone-local" @input="handlePhoneInput" @paste="handlePhonePaste" />
        </div>
      </div>

      <div class="form-grid">
        <v-text-field v-model="stripeCustomerId" :label="$t('dashboard.vserver.admin.import.stripeCustomer')" variant="outlined" density="compact" hide-details class="form-input wide" />
        <v-text-field v-model="cancelStripeSubId" :label="$t('dashboard.vserver.admin.import.cancelSub')" variant="outlined" density="compact" hide-details class="form-input wide" />
      </div>
      <p class="hint">{{ $t('dashboard.vserver.admin.import.cancelSubHint') }}</p>
    </div>

    <!-- VM + pricing -->
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.vserver.admin.import.vmTitle') }}</h3>
      </div>
      <div class="form-grid">
        <v-select v-if="!nodesUnavailable" v-model="vm.proxmox_node" :items="nodeItems" :loading="!nodesLoaded" :label="$t('dashboard.vserver.admin.import.node')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-else v-model="vm.proxmox_node" :label="$t('dashboard.vserver.admin.import.node')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model.number="vm.proxmox_vmid" type="number" :label="$t('dashboard.vserver.admin.import.vmid')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model="vm.ipv4" :label="$t('dashboard.vserver.admin.import.ipv4')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="vm.ipv6_block" :label="$t('dashboard.vserver.admin.import.ipv6Block')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="vm.subnet" :label="$t('dashboard.vserver.admin.import.subnet')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="vm.gateway" :label="$t('dashboard.vserver.admin.import.gateway')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="vm.hostname" :label="$t('dashboard.vserver.admin.import.hostname')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-text-field v-model="vm.template_slug" :label="$t('dashboard.vserver.admin.import.template')" variant="outlined" density="compact" hide-details class="form-input" />
      </div>
      <p v-if="nodesUnavailable" class="hint">{{ $t('dashboard.vserver.admin.import.nodeManualHint') }}</p>
      <p class="hint">{{ $t('dashboard.vserver.admin.import.ipv6BlockHint') }}</p>

      <div class="form-grid">
        <v-text-field v-model.number="monthlyGrossEuro" type="number" :label="$t('dashboard.vserver.admin.import.priceEuro')" variant="outlined" density="compact" hide-details class="form-input" />
        <v-select v-model="billingFreq" :items="billingFreqItems" :label="$t('dashboard.vserver.admin.import.billingFreq')" variant="outlined" density="compact" hide-details class="form-input" />
      </div>
      <p class="hint">{{ $t('dashboard.vserver.admin.import.priceHint') }}</p>

      <div class="form-grid">
        <v-text-field v-model.number="specs.vcpu" type="number" :label="$t('dashboard.vserver.admin.import.vcpu')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model.number="specs.memory_mb" type="number" :label="$t('dashboard.vserver.admin.import.memoryMb')" variant="outlined" density="compact" hide-details class="form-input narrow" />
        <v-text-field v-model.number="specs.disk_gb" type="number" :label="$t('dashboard.vserver.admin.import.diskGb')" variant="outlined" density="compact" hide-details class="form-input narrow" />
      </div>
      <p class="hint">{{ $t('dashboard.vserver.admin.import.specsHint') }}</p>

      <div class="actions">
        <button type="button" class="btn-create" :disabled="!canSubmit" @click="submit">
          <v-icon icon="mdi-import" size="18" />
          {{ $t('dashboard.vserver.admin.import.submit') }}
        </button>
      </div>
    </div>

    <!-- Result -->
    <div v-if="result" class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.vserver.admin.import.resultTitle') }}</h3>
        <span class="status-chip" :class="result.existing ? 'status-warning' : 'status-success'">
          {{ result.existing ? $t('dashboard.vserver.admin.import.alreadyImported') : $t('dashboard.vserver.admin.import.success') }}
        </span>
      </div>
      <div class="result-grid">
        <span class="r-label">{{ $t('dashboard.vserver.admin.import.rVserver') }}</span>
        <span class="mono">#{{ result.vserver_id }}</span>
        <span class="r-label">{{ $t('dashboard.vserver.admin.import.rSubscription') }}</span>
        <span class="mono">#{{ result.subscription_id }}</span>
        <span class="r-label">{{ $t('dashboard.vserver.admin.import.rAccount') }}</span>
        <span class="mono">#{{ result.account_id }}</span>
        <span class="r-label">{{ $t('dashboard.vserver.admin.import.rStripeSub') }}</span>
        <span class="mono">{{ result.stripe_subscription_id || '-' }}</span>
        <span class="r-label">{{ $t('dashboard.vserver.admin.import.rTrialEnd') }}</span>
        <span class="mono">{{ result.trial_end }}</span>
      </div>

      <v-alert v-if="result.created_account" type="info" variant="tonal" class="result-alert">
        {{ $t('dashboard.vserver.admin.import.createdAccountNote') }}
      </v-alert>
      <v-alert v-if="result.canceled_stripe_subscription" type="info" variant="tonal" class="result-alert">
        {{ $t('dashboard.vserver.admin.import.canceledNote', { id: result.canceled_stripe_subscription }) }}
      </v-alert>
      <v-alert
        v-if="result.other_active_stripe_subscriptions && result.other_active_stripe_subscriptions.length"
        type="warning"
        variant="tonal"
        class="result-alert"
      >
        {{ $t('dashboard.vserver.admin.import.otherSubsWarning') }}
        <span class="mono">{{ result.other_active_stripe_subscriptions.join(', ') }}</span>
      </v-alert>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  align-items: start;
  margin-bottom: 10px;

  &.newacc {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 900px) {
  .form-grid,
  .form-grid.newacc {
    grid-template-columns: 1fr 1fr;
  }
}

.form-input.wide {
  grid-column: span 2;
}

.phone-row {
  display: flex;
  gap: 8px;
}

.phone-code {
  flex: 0 0 130px;
}

.phone-local {
  flex: 1 1 auto;
}

.hint {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 4px 0 0;
}

.actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #00b0ff, #1fe2d4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.result-grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 18px;
  align-items: center;
}

.r-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
}

.result-alert {
  margin-top: 14px;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}
</style>
